import { useEffect, useState, useCallback, useRef } from 'react';

/**
 * @deprecated use useIntervalV2
 */
export const useInterval = (
  callback: () => unknown,
  delay: number,
  dependencies = []
) => {
  const savedCallback = useRef(callback);

  useEffect(() => {
    savedCallback.current = callback;
    savedCallback.current();
  }, [callback, ...dependencies]);

  useEffect(() => {
    if (delay === null) {
      return;
    }

    const id = setInterval(() => savedCallback.current(), delay);

    return () => clearInterval(id);
  }, [delay, ...dependencies]);
};

interface UseIntervalProps {
  callback: () => void;
  timeout?: number;
}

interface UseIntervalReturn {
  stop: () => void;
  start: () => void;
  restart: () => void;
  counter: number;
  isActive: boolean;
}

type TimeoutIdx = ReturnType<typeof setTimeout>;

export const useIntervalV2 = ({
  callback,
  timeout = 3000,
}: UseIntervalProps): UseIntervalReturn => {
  const [counter, setCounter] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [restartIdx, setRestartIdx] = useState<number>(0);

  useEffect(() => {
    callback();
    setCounter(1);
  }, [callback]);

  const run = useCallback(() => {
    return setTimeout(() => {
      setCounter((prev) => prev + 1);
      callback();
    }, timeout);
  }, [callback, timeout]);

  const stop = useCallback(() => {
    setIsActive(false);
  }, []);

  const start = useCallback(() => {
    setIsActive(true);
  }, []);

  const restart = useCallback(() => {
    setRestartIdx((prev) => prev + 1);
  }, []);

  useEffect(() => {
    let t: TimeoutIdx;
    if (isActive) {
      t = run();
    }

    return () => {
      clearTimeout(t);
    };
  }, [run, counter, isActive, restartIdx]);

  return { stop, start, restart, counter, isActive };
};
